/**
 * Estimation de l'horloge serveur côté client. Chaque message d'état porte
 * le temps de simulation ; entre deux messages, le client extrapole à la
 * vitesse courante et corrige doucement la dérive (pas de saut visible).
 * Le rendu se place légèrement en retard (`delay`) pour que les changements
 * de mouvement soient déjà connus quand il les affiche.
 */
import type { ClockWire } from '@snowchain/shared';

export class ServerClock {
  private baseSim = 0;
  private baseGame = 0;
  private baseAt = 0;
  private rate = 0;
  scale = 20;
  speed = 1;
  running = false;
  tick = 0;
  private ready = false;
  /** Retard de rendu en secondes réelles. */
  delay = 0.15;

  sample(c: ClockWire, at: number): void {
    const rate = c.running ? c.speed : 0;
    if (!this.ready || rate !== this.rate || Math.abs(this.simAt(at) - c.sim) > 1.5 * Math.max(1, rate)) {
      this.baseSim = c.sim;
      this.baseAt = at;
      this.ready = true;
    } else {
      // Correction progressive de la dérive.
      const err = c.sim - this.simAt(at);
      this.baseSim = this.simAt(at) + err * 0.15;
      this.baseAt = at;
    }
    this.rate = rate;
    this.baseGame = c.game - (c.sim - this.baseSim) * c.scale;
    this.scale = c.scale;
    this.speed = c.speed;
    this.running = c.running;
    this.tick = c.tick;
  }

  private simAt(at: number): number {
    return this.baseSim + ((at - this.baseAt) / 1000) * this.rate;
  }

  /** Temps de simulation estimé côté serveur à l'instant `at` (performance.now). */
  simNow(at = performance.now()): number {
    return this.simAt(at);
  }

  /** Temps de simulation affiché (légèrement en retard pour lisser). */
  renderSim(at = performance.now()): number {
    return this.simAt(at) - this.delay * this.rate;
  }

  gameNow(at = performance.now()): number {
    return this.baseGame + (this.simAt(at) - this.baseSim) * this.scale;
  }
}
